import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../contexts/CartContext";
import { CATEGORY_MAP } from "../data/categories";
import { BACKEND_URL } from "../lib/api";
import { variantLabels } from "../lib/variants";

function resolveImage(url, fallback) {
    if (!url) return fallback;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api")) return `${BACKEND_URL}${url}`;
    return url;
}

export default function ProductCard({ item }) {
    const [variant, setVariant] = useState("sandwich");
    const [qty, setQty] = useState(1);
    const [notes, setNotes] = useState("");
    const { addItem } = useCart();

    const hasMeal = item.meal_price && item.meal_price > 0;
    const price = variant === "meal" && hasMeal ? item.meal_price : item.price;
    const categoryImage = CATEGORY_MAP[item.category]?.image;
    const itemImage = resolveImage(item.image, categoryImage);
    const labels = variantLabels(item.category);

    const handleAdd = () => {
        addItem({
            item_id: item.id,
            name: item.name,
            category: item.category,
            price,
            quantity: qty,
            variant: hasMeal ? variant : undefined,
            notes: notes.trim(),
        });
        toast.success("تمت إضافته للسلة", {
            description: `${item.name}${hasMeal ? ` (${variant === "meal" ? labels.large : labels.small})` : ""}`,
        });
        setQty(1);
        setNotes("");
    };

    return (
        <div className="surface-card overflow-hidden fade-up" data-testid={`product-card-${item.id}`}>
            <div className="flex">
                <div
                    className="w-28 h-28 sm:w-32 sm:h-32 flex-shrink-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${itemImage}')` }}
                    aria-hidden
                />
                <div className="flex-1 p-3 sm:p-4 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                        <h3 className="font-extrabold text-text-main text-base sm:text-lg leading-snug" data-testid={`product-name-${item.id}`}>
                            {item.name}
                        </h3>
                        <div className="text-brand font-black text-base sm:text-lg whitespace-nowrap" data-testid={`product-price-${item.id}`}>
                            {price} <span className="text-xs font-bold">ر.س</span>
                        </div>
                    </div>

                    {hasMeal && (
                        <div className="mt-2 flex gap-1.5 text-xs">
                            <button
                                onClick={() => setVariant("sandwich")}
                                data-testid={`variant-sandwich-${item.id}`}
                                className={`px-2.5 py-1 rounded-full font-bold transition ${
                                    variant === "sandwich"
                                        ? "bg-brand text-white"
                                        : "bg-[var(--border-subtle)] text-text-muted"
                                }`}
                            >
                                {labels.small} {item.price} ر.س
                            </button>
                            <button
                                onClick={() => setVariant("meal")}
                                data-testid={`variant-meal-${item.id}`}
                                className={`px-2.5 py-1 rounded-full font-bold transition ${
                                    variant === "meal"
                                        ? "bg-brand text-white"
                                        : "bg-[var(--border-subtle)] text-text-muted"
                                }`}
                            >
                                {labels.large} {item.meal_price} ر.س
                            </button>
                        </div>
                    )}

                    <input
                        type="text"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="ملاحظات (بدون بصل، حار، إلخ)"
                        data-testid={`product-notes-${item.id}`}
                        className="mt-2 w-full text-xs sm:text-sm bg-[var(--bg)] border border-[var(--border-subtle)] rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
                    />

                    <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1 bg-[var(--bg)] border border-[var(--border-subtle)] rounded-full p-1">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="w-7 h-7 rounded-full bg-white text-text-main flex items-center justify-center"
                                data-testid={`qty-decrease-${item.id}`}
                                aria-label="إنقاص"
                            >
                                <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-6 text-center font-extrabold text-sm" data-testid={`qty-value-${item.id}`}>
                                {qty}
                            </span>
                            <button
                                onClick={() => setQty((q) => q + 1)}
                                className="w-7 h-7 rounded-full bg-white text-text-main flex items-center justify-center"
                                data-testid={`qty-increase-${item.id}`}
                                aria-label="زيادة"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <button
                            onClick={handleAdd}
                            data-testid={`add-to-cart-${item.id}`}
                            className="btn-brand text-sm px-3 py-2"
                        >
                            <i className="fa-solid fa-cart-plus me-1" /> أضف للسلة
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
